import Bugsnag from '@bugsnag/js'
import getConfig from 'next/config'
import styled from 'styled-components'
import { Trans, useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'

import Button from 'shared/components/Button'
import Image from 'shared/components/Image'
import Modal from 'shared/components/Modal'
import PaymentType from 'shared/enums/PaymentType'
import { AffirmLogo } from 'shared/icons/PaymentMethods/AffirmLogo'
import { AfterPayLogo } from 'shared/icons/PaymentMethods/AfterPayLogo'
import { ClientOnlyPortal } from 'shared/components/ClientOnlyPortal'
import { CloseX } from 'shared/components/Icons/SVGIcons/CloseX'
import { KlarnaLogo } from 'shared/icons/PaymentMethods/KlarnaLogo'
import { PayPalLogo } from 'shared/icons/PaymentMethods/PayPalLogo'
import { ProductQA } from 'shared/dataAttributes'
import { colors, media, zIndex } from 'shared/lib'
import { useWindowSize } from 'shared/hooks/useWindowSize'

const TP = 'checkout.panels.PaymentMethod.ThirdPartyPaymentOption'

type ThirdPartyPaymentOptionProps = {
  paymentType: PaymentType
  price?: number
}

export const ThirdPartyPaymentOption = ({ paymentType, price }: ThirdPartyPaymentOptionProps) => {
  const { publicRuntimeConfig } = getConfig()
  const { klarnaClientId, klarnaOsmUrl } = publicRuntimeConfig
  const tv = useTranslation()
  const { isMobile } = useWindowSize()

  const defaultPromotion = {
    learnMoreText: '',
    learnMoreUrl: '',
    paymentInformation: '',
  }

  const paymentLabel = (() => {
    switch (paymentType) {
      case PaymentType.Affirm:
        return 'Affirm'
      case PaymentType.AfterPay:
        return 'Afterpay'
      case PaymentType.Klarna:
        return 'Klarna'
      case PaymentType.PayPalv2:
        return 'PayPal'
      default:
        return ''
    }
  })()

  const paymentLogo = (() => {
    switch (paymentType) {
      case PaymentType.Affirm:
        return <AffirmLogo />
      case PaymentType.AfterPay:
        return <AfterPayLogo />
      case PaymentType.Klarna:
        return <KlarnaLogo />
      case PaymentType.PayPalv2:
        return <PayPalLogo />
      default:
        return ''
    }
  })()

  const [promotion, setPromotion] = useState(defaultPromotion)
  const [isLearnMoreModalOpen, setIsLearnMoreModalOpen] = useState(false)

  const handleClickLearnMore = () => {
    setIsLearnMoreModalOpen(true)
  }
  const handleCloseLearnMoreModal = () => {
    setIsLearnMoreModalOpen(false)
  }

  const paymentInformation = (() => {
    switch (paymentType) {
      case PaymentType.Affirm:
        return (
          <PaymentInformation
            className="affirm-as-low-as"
            data-amount={`${price}`}
            data-page-type="payment"
          />
        )
      case PaymentType.AfterPay:
        return (
          <PaymentInformation>
            <Trans
              i18nKey={`${TP}.afterpayPromotional`}
              t={tv.t}
              defaults="4 Interest-free payments <0>Learn more</0> (US only)"
              components={[
                <Button
                  buttonType="link"
                  data-testid="afterpay-promotion"
                  key="afterpay-promotion"
                  onClick={handleClickLearnMore}
                />,
              ]}
            />
            {isLearnMoreModalOpen && (
              <ClientOnlyPortal id="afterpay-learn-more-modal" selector=".main-page-layout">
                <Modal
                  data-testid="afterpay-modal"
                  isOpen={isLearnMoreModalOpen}
                  onClose={handleCloseLearnMoreModal}
                  widthOverride="auto"
                >
                  <ContentWrapper data-testid="afterpay-modal-content">
                    <XIconWrapper
                      data-qa={ProductQA.AftepayPromotionCloseIcon}
                      data-testid="afterpay-modal-x"
                      onClick={handleCloseLearnMoreModal}
                    >
                      <CloseX height="15" width="15" />
                    </XIconWrapper>
                    <a
                      href="https://www.afterpay.com/purchase-payment-agreement"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {isMobile ? (
                        <Image
                          alt="afterpay promotional us-mobile"
                          className="mobile"
                          height="896"
                          src="https://static-us.afterpay.com/assets/modal/us-mobile@2x.png"
                          width="335"
                        />
                      ) : (
                        <Image
                          alt="afterpay promotional us-desktop"
                          className="desktop"
                          height="366"
                          src="https://static-us.afterpay.com/assets/modal/us@2x.png"
                          width="500"
                        />
                      )}
                    </a>
                  </ContentWrapper>
                </Modal>
              </ClientOnlyPortal>
            )}
          </PaymentInformation>
        )
      case PaymentType.Klarna:
        return (
          <PaymentInformation>
            {promotion?.paymentInformation}
            {promotion?.learnMoreText && promotion?.learnMoreUrl && (
              <>
                &nbsp;
                <Button buttonType="link" onClick={handleClickLearnMore}>
                  {promotion?.learnMoreText}
                </Button>
                {isLearnMoreModalOpen && (
                  <ClientOnlyPortal id="klarna-learn-more-modal" selector=".main-page-layout">
                    <Modal
                      data-testid="klarna-modal"
                      isOpen={isLearnMoreModalOpen}
                      onClose={handleCloseLearnMoreModal}
                      widthOverride="auto"
                    >
                      <ContentWrapper>
                        <XIconWrapper
                          data-qa={ProductQA.KlarnaPromotionCloseIcon}
                          data-testid="klarna-modal-x"
                          onClick={handleCloseLearnMoreModal}
                        >
                          <CloseX height="15" width="15" />
                        </XIconWrapper>
                        <CustomIframe src={promotion?.learnMoreUrl} />
                      </ContentWrapper>
                    </Modal>
                  </ClientOnlyPortal>
                )}
              </>
            )}
          </PaymentInformation>
        )
      default:
        return <PaymentInformation>{promotion?.paymentInformation}</PaymentInformation>
    }
  })()

  useEffect(() => {
    if (klarnaClientId && klarnaOsmUrl && paymentType === PaymentType.Klarna && price) {
      const urlParams = new URLSearchParams({
        channel: 'web',
        client_id: klarnaClientId,
        locale: 'en-US',
        placement_key: 'credit-promotion-badge',
        purchase_amount: `${price}`,
      })
      const getKlarnaPromotional = async () => {
        try {
          const response = await fetch(`${klarnaOsmUrl}messaging?${urlParams}`)
          const data = await response.json()
          setPromotion({
            learnMoreText: data?.content?.nodes?.[1]?.label,
            learnMoreUrl: data?.content?.nodes?.[1]?.url,
            paymentInformation: data?.content?.nodes?.[0]?.value,
          })
        } catch (error) {
          Bugsnag.notify(error)
        }
      }
      void getKlarnaPromotional()
    }
  }, [klarnaClientId, klarnaOsmUrl, paymentType, price])

  return (
    <Container>
      {paymentLogo}
      <VerticalContainer>
        <div>{paymentLabel}</div>
        {paymentInformation}
      </VerticalContainer>
    </Container>
  )
}

const Container = styled.div`
  align-items: center;
  display: flex;
  min-height: 36px;
`
const CustomIframe = styled.iframe`
  border: none;
  height: 850px;
  width: 550px;

  ${media.medium`
    height: 760px;
  `}
`
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  overflow-y: auto;
  position: relative;
  width: 100%;
`
const PaymentInformation = styled.div`
  color: ${colors.FC2_GREY};
  font-weight: 400;
  margin-top: 2px;

  a {
    color: ${colors.FC2_GREY};
  }
`
const VerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 16px;
`
const XIconWrapper = styled.div`
  cursor: pointer;
  padding: 20px;
  position: absolute;
  right: 0;
  z-index: ${zIndex.justOverNav};
`
